import { Popover, VStack, HStack, Box, Text, Checkbox, CheckboxGroup, Input, InputGroup, InputRightElement } from '@chakra-ui/react';
import { FilterPopoverButton, FilterPopoverContent } from './FilterPopover';
import { ICourse } from '@/shared/ICourse';
import { WorkflowService } from '@/shared/WorkflowService';
import { WorkflowTextService } from '@/shared/WorkflowTextService';
import React from 'react';
import { FiSearch } from 'react-icons/fi';

type StepStatusOption = {
  label: string;
  value: string;
  count: number;
  courseStep: string;
};

export const CheckboxFilterStepStatus = (props: {
  allCourses: ICourse[];
  stepValue: string[] | undefined;
  onSetStepFilter: (value: string[] | undefined) => void;
}) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  
  // Create a map of all unique course steps and their counts
  const stepStatusMap = new Map<string, StepStatusOption>();
  
  props.allCourses?.forEach(course => {
    if (course.CourseStep) {
      const courseStep = course.CourseStep;
      
      if (stepStatusMap.has(courseStep)) {
        stepStatusMap.get(courseStep)!.count++;
      } else {
        stepStatusMap.set(courseStep, {
          label: WorkflowService.getCourseBadgeLabel(course),
          value: courseStep,
          count: 1,
          courseStep: courseStep
        });
      }
    }
  });

  const options = Array.from(stepStatusMap.values()).sort((a, b) => 
    WorkflowTextService.compareCourseSteps(a.value, b.value)
  );

  // Filter options based on search query
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle selection changes
  const handleSelectionChange = (selectedValues: string[]) => {
    props.onSetStepFilter(selectedValues.length > 0 ? selectedValues : undefined);
  };

  // Calculate current selected values
  const currentSelection = React.useMemo(() => {
    return props.stepValue || [];
  }, [props.stepValue, options]);

  const hasSelection = (props.stepValue?.length || 0) > 0;

  return (
    <Popover isLazy placement="bottom-start">
      <FilterPopoverButton label="Step" selected={hasSelection} />
      <FilterPopoverContent>
        <InputGroup size="md" pb="1">
          <Input
            placeholder="Search..."
            rounded="md"
            focusBorderColor="blue.500"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <InputRightElement pointerEvents="none" color="gray.400" fontSize="lg">
            <FiSearch />
          </InputRightElement>
        </InputGroup>
        <CheckboxGroup value={currentSelection} onChange={handleSelectionChange}>
            <VStack spacing="2" align="stretch">
              {filteredOptions.map((option) => (
                <Checkbox key={option.value} value={option.value} colorScheme="blue">
                  <HStack spacing="1" align="center" width="full">
                    <Box
                      width="12px"
                      height="12px"
                      borderRadius="2px"
                      bg={`${WorkflowTextService.getCourseBadgeColor(option.courseStep)}.200`}
                      border="1px solid"
                      borderColor={`${WorkflowTextService.getCourseBadgeColor(option.courseStep)}.400`}
                      flexShrink={0}
                    />
                    <Text fontSize="sm" flex="1">
                      {WorkflowService.getCourseBadgeLabel({ CourseStep: option.courseStep } as ICourse)}
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                      ({option.count})
                    </Text>
                  </HStack>
                </Checkbox>
              ))}
            </VStack>
          </CheckboxGroup>
        </FilterPopoverContent>
    </Popover>
  );
};
